/*
 * Copyright (C) 2014 Spanning Cloud Apps.  All rights reserved.
 */
/**
 * Service module for common sobject services/utilities.
 */
'use strict';

angular
  .module('services.sobject', ['http.batch', 'http.queue', 'http.sobject', 'services.auth', 'services.util'])
  /**
   * A service with common sobject utilities.
   */
  .service('sObjectUtils', function (httpSObject, authFactory, httpQueue, httpBatch, UtilService, $q, $filter) {
    return {
      /**
       * Used to get access the array of versions that are available for this object found via
       * the state params identified by the objectId and objectType properties.  Note the rest
       * service will only be called on the first call of this function unless the force parameter
       * is set to true.
       *
       * @param {Boolean} force - True to force a refresh of the data with the server.
       * @returns {Promise} - A promise that resolves into the array of versions.
       */
      getVersions: function (force) {
        let deferred, me;
        if (!this.versions || force) {
          me = this;

          const params = UtilService.extractQueryParams();

          me.objectId = params.objectId;
          me.objectName = params.objectType;

          return httpSObject.getVersions($filter('15To18')(me.objectId), me.objectName).then(function (versions) {
            me.versions = versions;
            if (versions.length > 0) {
              me.oldestLastModified = versions[versions.length - 1].lastModified;
              me.newestLastModified = versions[0].lastModified;
            }
            return me.versions;
          });
        } else {
          deferred = $q.defer();
          deferred.resolve(this.versions);
          return deferred.promise;
        }
      },

      /**
       * Utility for getting the next version from the one currently set.
       *
       * @returns {Promise} - A Promise that resolves after the next version has been set.
       */
      getNextVersion: function () {
        var me = this;

        if (_.isNumber(me.index)) {
          return this.getVersions().then(function (versions) {
            if (me.index !== versions.length - 1) {
              me.index++;
              me.currentVersion = versions[me.index];
              return me.currentVersion;
            }
          });
        } else {
          // Return a fake promise here, so we will hit then handler in picker.js and hide loading mask
          return $q.when();
        }
      },

      /**
       * Utility for getting the previous version.
       *
       * @returns {Promise} - A Promise that resolves after the previous version
       * has bee set.
       */
      getPreviousVersion: function () {
        var me = this;

        if (_.isNumber(me.index)) {
          return this.getVersions().then(function (versions) {
            if (me.index !== 0) {
              me.index--;
              me.currentVersion = versions[me.index];
              return me.currentVersion;
            }
          });
        } else {
          // Return a fake promise here, so we will hit then handler in picker.js and hide loading mask
          return $q.when();
        }
      },

      /**
       * Utility function to get the latest version on a particular date.
       *
       * @param {Date} versionDate - The date to specify what day to search.
       * @returns {Promise} - A Promise that resolves after the latest version
       * on the provided date has been set.  Will be a version with the noVersion
       * property set to true if no versions were found on that date.
       */
      getVersionByDate: function (versionDate) {
        var me = this;
        this.getVersions().then(function (versions) {
          var versionToReturn = {
            noVersion: true,
            lastModified: versionDate
          };
          for (var i = versions.length - 1; i >= 0; i--) {
            if (moment(versionDate).isSame(versions[i].lastModified, 'day')) {
              versionToReturn = versions[i];
              me.index = i;
              break;
            }
          }
          me.currentVersion = versionToReturn;
          return versionToReturn;
        });
      },

      /**
       * Function use to get the most recent version of an sobject and set it as the
       * current version.
       */
      getLatestVersion: function () {
        var me = this;

        return this.getVersions().then(function (versions) {
          if (versions.length > 0) {
            me.currentVersion = _.last(versions);
            me.index = versions.length - 1;
          } else {
            me.currentVersion = {
              noBackups: true
            };
          }
          return me.currentVersion;
        });
      },

      /**
       * Get the diff for the version that is set as the current version.
       *
       * @returns {Promise} - A promise that resolves into a diff object.
       * If the diff currently set to the currentDiff object is up to date it will
       * be returned instead of calling the http service.
       */
      getCurrentDiff: function () {
        var me = this,
          deferred;
        if (this.currentVersion.noVersion) {
          // If no version is set then return an empty diff.
          deferred = $q.defer();
          deferred.resolve({
            attachments: [],
            diffs: [],
            selectedAttachments: [],
            selectedItems: []
          });
          return deferred.promise;
        } else {
          if (this.currentVersion.lastModified !== this.diffLastMod) {
            return httpSObject
              .getDiff(this.currentVersion.sObjectRecordId, this.currentVersion.sObjectRecordBatchId)
              .then(function (diff) {
                //TODO: can be removed after diff rest service is updated.
                diff.attachments = diff.attachments || [];
                me.currentDiff = diff;
                me.selectedItems = [];
                me.selectedAttachments = [];
                me.diffLastMod = me.currentVersion.lastModified;
                return diff;
              });
          } else {
            // If requesting the currently loaded diff return it.
            deferred = $q.defer();
            deferred.resolve(this.currentDiff);
            return deferred.promise;
          }
        }
      },

      /**
       * Submit a restore to be queued.
       *
       * @returns {Boolean} - True for a successful submit, false for a failed submit.
       */
      submitRestore: function () {
        return httpQueue.submitSObjectRestore(
          this.currentVersion.sObjectRecordId,
          this.currentVersion.sObjectRecordBatchId,
          this.selectedItems,
          { attachments: this.selectedAttachments }
        );
      },

      /**
       * Get the latest object restore object.
       *
       * @returns {Promise} - A promise that resolves into the batch object
       * for the most recent object restore.
       */
      getLatestObjectRestore: function () {
        return httpBatch.getLatestObjectRestore();
      },

      /**
       * Function used to determine if the current object is a single version object
       * that has no differences from the current object based on the diff response.
       *
       * @returns {boolean} - True if the current object is a single version with no differences. False otherwise.
       */
      isSingleUnchangedVersion: function () {
        var me = this;
        return this.getVersions().then(function (versions) {
          if (versions.length === 1) {
            return me.getCurrentDiff().then(function (diff) {
              if (diff.attachments.length === 0 && diff.diffs.length === 0) {
                return true;
              }
              return false;
            });
          }
          return false;
        });
      },

      /**
       * Returns the list of Salesforce object types.  Note the REST service will only be called on the first call of
       * this function unless the force parameter is set to true.
       *
       * @param {Boolean} [force] - true to force a refresh of the data with the server.
       * @returns {Promise<object[]>} - A promise of the object type list.
       */
      getTypes: function (force) {
        if (this.objectTypes && !force) {
          return $q.when(this.objectTypes);
        }

        return httpSObject.getTypes().then((types) => {
          this.objectTypes = types;
          return types;
        });
      },

      // Value used to track a sobject version.
      currentVersion: {},
      // The lastModified date of the most recent version.
      newestLastModified: null,
      // the lastModified date of the oldest version.
      oldestLastModified: null,
      // The diff object for the current version.
      currentDiff: null,
      // The fields that have been selected from the version.
      selectedItems: [],
      // The last modified time of the currentDiff value.
      diffLastMod: null,
      // The object id of the object being examined.
      objectId: null,
      // The object name for the object being examined.
      objectName: null,
      // The list of selected attachments.
      selectedAttachments: [],
      // The cache of object types
      objectTypes: null
    };
  });
